import LocalstorageLastUrl from '_services/LocalStorage/Lasturl';
import { useRef } from 'react';
import {
    NavigateOptions,
    To,
    useNavigate as useReactNavigate,
} from 'react-router-dom';

/**
 * NAVIGATION CONFIRMATION SYSTEM DOCUMENTATION
 *
 * The useNavigate hook supports optional confirmation dialogs before navigation.
 *
 * HOW IT WORKS:
 * 1. Components can register a global confirmation handler using setNavigationConfirmationHandler()
 * 2. Navigation calls can opt-in to respect this handler using shouldAwaitConfirmation: true
 * 3. Both conditions must be true for confirmation to show
 *
 * USAGE EXAMPLES:
 *
 * // Regular navigation (no confirmation)
 * navigate('/some-path')
 *
 * // Navigation that respects global confirmation handler (if one exists)
 * navigate('/some-path', { shouldAwaitConfirmation: true })
 *
 * // Navigation that bypasses confirmation (same as default)
 * navigate('/some-path', { shouldAwaitConfirmation: false })
 *
 * SETTING UP CONFIRMATION:
 * Components with unsaved changes can register a handler:
 *
 * setNavigationConfirmationHandler(async () => {
 *   return window.confirm('You have unsaved changes. Continue?');
 * });
 *
 * // Clear when no longer needed
 * setNavigationConfirmationHandler(null);
 *
 * WHEN CONFIRMATION SHOWS:
 * - shouldAwaitConfirmation: true AND globalConfirmationHandler exists
 *
 * WHEN CONFIRMATION DOES NOT SHOW:
 * - shouldAwaitConfirmation: false (default)
 * - shouldAwaitConfirmation: true BUT no globalConfirmationHandler
 * - Any other page where no component has set a handler
 */

// Extend the base NavigateOptions with optional confirmation capabilities
interface ExtendedNavigateOptions extends NavigateOptions {
    shouldAwaitConfirmation?: boolean;
}

type Return = (to: To | number, options?: ExtendedNavigateOptions) => void;

// Global flag to track if we've restored scroll for current navigation
let hasRestoredScrollForCurrentNavigation = false;

// Global confirmation handler - optional feature
let globalConfirmationHandler: (() => Promise<boolean>) | null = null;

export const setNavigationConfirmationHandler = (
    handler: (() => Promise<boolean>) | null,
): void => {
    globalConfirmationHandler = handler;
};

type StripeBackNavigationInfo = {
    primary: string;
    secondary?: string;
};

const getStripeBackNavigationInfo = (): StripeBackNavigationInfo | null => {
    try {
        const previousUrl = document.referrer ?? '';
        if (!previousUrl || !previousUrl.includes('stripe.com')) {
            return null;
        }

        const historyState = window.history?.state as { idx?: number } | null;
        if (historyState?.idx !== 0) {
            return null;
        }

        const urlsForBack = LocalstorageLastUrl.get();
        if (!urlsForBack.length) {
            return null;
        }

        const [primary, secondary] = urlsForBack;
        if (!primary) {
            return null;
        }

        return { primary, secondary };
    } catch (error) {
        console.error('Stripe back navigation detection failed:', error);
        return null;
    }
};

/**
 * Extend default react-router navigate hook,
 * to implement logic for storing scroll position to history.state before going to new path
 * also supports optional navigation confirmation
 */
const useNavigate = (): Return => {
    const doNavigate = useReactNavigate();
    const isNavigating = useRef(false);

    /**
     * Capture the positions of y scrolls
     */
    const captureXScrollPositions = () => {
        const sliders = document.getElementsByClassName('items-slider-wrapper');

        const positions = {};
        for (let index = 0; index < sliders.length; index++) {
            const sliderElement = sliders[index]?.firstChild as HTMLDivElement;

            if (sliderElement && sliderElement.hasAttribute('id')) {
                const positionFromLeft = sliderElement.scrollLeft;
                const scrollableId = sliderElement.id;

                positions[scrollableId] = positionFromLeft;
            }
        }

        return positions;
    };

    const navigate = async (
        initialTo: To | number,
        options?: ExtendedNavigateOptions,
    ) => {
        if (isNavigating.current) return;
        isNavigating.current = true;

        try {
            // Only await confirmation if explicitly requested and handler is available
            if (
                options?.shouldAwaitConfirmation === true &&
                globalConfirmationHandler
            ) {
                try {
                    const shouldContinue = await globalConfirmationHandler();
                    if (!shouldContinue) {
                        return;
                    }
                } catch (error) {
                    console.error('Navigation confirmation error:', error);
                    return;
                }
            }

            let to: To | number = initialTo;
            const isBackNavigation = typeof to === 'number' && to < 0;

            if (isBackNavigation && to === -1) {
                const stripeBackInfo = getStripeBackNavigationInfo();

                if (stripeBackInfo) {
                    const { primary, secondary } = stripeBackInfo;

                    if (secondary) {
                        window.history.pushState(
                            null,
                            document.title,
                            secondary,
                        );
                    }

                    if (primary.includes('http')) {
                        LocalstorageLastUrl.remove();
                        window.location.href = primary;
                        return;
                    }

                    LocalstorageLastUrl.remove();
                    to = primary;
                }
            }

            const y = window.scrollY;
            const { history } = window;
            const positions = captureXScrollPositions();

            history.replaceState(
                { ...history.state, y, ...positions },
                document.title,
                window.location.toString(),
            );

            LocalstorageLastUrl.add(window.location.pathname);

            // Store the scroll position we want to restore
            const state = window.history.state;
            const scrollToRestore = state?.y;
            hasRestoredScrollForCurrentNavigation = false;

            // Strip custom properties before passing to react-router
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { shouldAwaitConfirmation, ...reactRouterOptions } =
                options || {};

            // Navigate first
            if (typeof to === 'number') {
                doNavigate(to);
            } else {
                doNavigate(to as To, reactRouterOptions);
            }

            if (scrollToRestore && !hasRestoredScrollForCurrentNavigation) {
                window.scrollTo(0, scrollToRestore);
                hasRestoredScrollForCurrentNavigation = true;
            }
        } finally {
            isNavigating.current = false;
        }
    };

    return navigate;
};

export default useNavigate;
